import DetailsHeader from "components/DetailsHeader";
import Div from "components/Div";
import Text from "components/Text";
import CustomerIcon from '@enerex/unified-ui-react/icons/CustomerIcon';
import DetailsHeaderSkeleton from "@enerex/unified-ui-react/skeleton/DetailsHeaderSkeleton";
import { ICompany } from "./types";
import React, { useState, useCallback, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Alert, Button, Badge, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar as faStarRegular } from "@fortawesome/free-regular-svg-icons";
import { faStar as faStarSolid } from "@fortawesome/free-solid-svg-icons";
import { Option } from "components/SearchableSelect";
import AsyncSelect from "components/UnifiedAsyncSelect";

import { DEV_URL, HUBSPOT_BASE_URL, rget, rpost, rput } from "utils/requests";
import { useAuth } from "providers/CustomAuthProvider";
import PlusIcon from '@enerex/unified-ui-react/icons/AddIcon';
import CloseIcon from "@enerex/unified-ui-react/icons/CloseIcon";
import EditIcon from '@enerex/unified-ui-react/icons/EditIcon';
import TeamHierarchyIcon from '@enerex/unified-ui-react/icons/TeamHierarchyIcon';
import ConfirmationModal from "@enerex/unified-ui-react/ConfirmationModal";
import AddUpdateParentModal from "./modals/AddUpdateParentModal";
import ViewHierarchyModal from "./modals/ViewHierarchyModal";
import HierarchyLogsModal from "./modals/HierarchyLogsModal";
import {
  updateCompanyHierarchy,
  getCompanyHierarchy,
  getCompanyHierarchyLogs,
} from "./api/hierarchy";
import { buildCompanyLogoUrl } from "utils/logo";
import { buildHierarchyTree } from "./modals/utils/hierarchyTree";
import useShowSnackbar from "hooks/useShowSnackbar";
import useGlobalPremiumFlag from "hooks/useGlobalPremiumFlag";

const modalSelectStyles = {
  menu: (base: any) => ({ ...base, marginTop: 0, zIndex: 9999 }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
};

const DOMAIN_TRANSFER_COMPANY_PAGE_SIZE = 50;

type DomainAssociation = {
  id: string;
  domainId: string;
  companyId: string;
  companyName: string;
  companyStatus?: string;
  domain?: string;
};

type DomainDependency = {
  id: string;
  type: "Contact" | "Brand";
  name: string;
  detail?: string;
  companyId?: string;
  companyName?: string;
};

type CustomerDetailsHeaderProps = {
  company?: ICompany | null;
  aliases?: any[];
  contacts?: any;
  license?: any[];
  domainRefreshKey?: number;
  onCompanyUpdate?: () => void | Promise<void>;
};

const renderHeaderSkeleton = () => <DetailsHeaderSkeleton />;

const CustomerDetailsHeader = ({
  company,
  license,
  domainRefreshKey,
  onCompanyUpdate,
}: CustomerDetailsHeaderProps) => {
  const { isSystemAdmin } = useAuth();
  const isPremium = useGlobalPremiumFlag();
  const snackbar = useShowSnackbar();
  const navigate = useNavigate();
  const [showParentModal, setShowParentModal] = useState(false);
  const [parentModalMode, setParentModalMode] = useState<"add" | "update">(
    "add"
  );
  const [selectedParent, setSelectedParent] = useState<Option | null>(null);
  const [parentError, setParentError] = useState<string>("");
  const [showHierarchyModal, setShowHierarchyModal] = useState(false);
  const [showHierarchyLogsModal, setShowHierarchyLogsModal] = useState(false);
  const [hierarchyLogs, setHierarchyLogs] = useState<any[]>([]);
  const [loadingHierarchyLogs, setLoadingHierarchyLogs] = useState(false);
  const [hierarchyTree, setHierarchyTree] = useState<any>(null);
  const [loadingHierarchy, setLoadingHierarchy] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [domains, setDomains] = useState<string[]>([]);
  const [domainIdMap, setDomainIdMap] = useState<Record<string, string>>({});
  const [domainBrandLogoMap, setDomainBrandLogoMap] = useState<Record<string, string>>({});
  const [showAllDomains, setShowAllDomains] = useState(false);
  const [primaryDomain, setPrimaryDomain] = useState<string | null>(null);
  const [isAddingDomain, setIsAddingDomain] = useState(false);
  const [newDomainValue, setNewDomainValue] = useState("");
  const [isDomainActionLoading, setIsDomainActionLoading] = useState(false);
  const [isDomainTransferLoading, setIsDomainTransferLoading] = useState(false);
  const [selectedDomainTransferCompany, setSelectedDomainTransferCompany] =
    useState<Option | null>(null);
  const [domainTransferCompanyOptions, setDomainTransferCompanyOptions] =
    useState<Option[]>([]);
  const [domainTransferCompanyLoading, setDomainTransferCompanyLoading] =
    useState(false);
  const [domainTransferError, setDomainTransferError] = useState("");
  const [domainConfirmModal, setDomainConfirmModal] = useState<{
    show: boolean;
    action: "remove" | "remove-mapping" | "primary" | null;
    domain: string;
  }>({ show: false, action: null, domain: "" });
  const [duplicateDomainModal, setDuplicateDomainModal] = useState<{
    show: boolean;
    domain: string;
    companies: DomainAssociation[];
  }>({ show: false, domain: "", companies: [] });
  const [selectedDuplicateDomainIds, setSelectedDuplicateDomainIds] = useState<
    Record<string, boolean>
  >({});
  const [domainDependencyModal, setDomainDependencyModal] = useState<{
    show: boolean;
    domain: string;
    contacts: DomainDependency[];
    brands: DomainDependency[];
  }>({ show: false, domain: "", contacts: [], brands: [] });
  const domainTransferCompanyPageRef = useRef(1);
  const domainTransferCompanySearchRef = useRef("");
  const domainTransferCompanyHasMoreRef = useRef(true);
  const domainTransferCompanyOptionsRef = useRef<Option[]>([]);
  const domainTransferCompanyLoadingRef = useRef(false);
  const domainTransferCompanyLoadMoreQueuedRef = useRef(false);
  const domainTransferCompanySearchTimerRef =
    useRef<NodeJS.Timeout | null>(null);
  const domainTransferCompanySearchResolveRef =
    useRef<((options: Option[]) => void) | null>(null);
  const domainTransferSelectRef = useRef<any>(null);

  const normalizeDomain = (value?: string | null) =>
    (value || "").trim().toLowerCase();

    const hasDomainSpecialCharacters = (value?: string | null) =>
    /[^a-z0-9.-]/i.test(value || "");
    
  const loadParentOptions = useCallback(
    async (inputValue: string): Promise<Option[]> => {
      if (!inputValue) {
        return [];
      }

      // Clear any existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Return a promise that resolves after debounce delay
      return new Promise((resolve) => {
        debounceTimerRef.current = setTimeout(async () => {
          try {
            const response = await rpost(
              `${DEV_URL}/api/company/SearchCompany`,
              {
                Search: inputValue,
                Limit: 50,
              }
            );

            if (response.data && response.data.data) {
              const options = response.data.data.map((c: any) => ({
                value: c,
                label: c.legalName,
              }));
              resolve(options);
            } else {
              resolve([]);
            }
          } catch (error) {
            // keep it silent for now but log for debugging
            // eslint-disable-next-line no-console
            console.error(
              "Error searching companies for parent selection",
              error
            );
            resolve([]);
          }
        }, 300); // 300ms debounce delay
      });
    },
    []
  );

  const syncDomainTransferSelectOptions = useCallback((options: Option[]) => {
    const asyncSelect = domainTransferSelectRef.current;
    if (!asyncSelect?.setState) {
      return;
    }

    asyncSelect.setState((state: any) => ({
      defaultOptions: options,
      loadedOptions:
        state?.inputValue && state?.loadedInputValue
          ? options
          : state?.loadedOptions,
    }));
  }, []);

  const setDomainTransferCompanyLoadingState = useCallback(
    (isLoading: boolean) => {
      domainTransferCompanyLoadingRef.current = isLoading;
      setDomainTransferCompanyLoading(isLoading);
    },
    []
  );

  const setPagedDomainTransferCompanyOptions = useCallback(
    (options: Option[]) => {
      domainTransferCompanyOptionsRef.current = options;
      setDomainTransferCompanyOptions(options);
      syncDomainTransferSelectOptions(options);
    },
    [syncDomainTransferSelectOptions]
  );

  const resetDomainTransferCompanyOptions = useCallback(() => {
    if (domainTransferCompanySearchTimerRef.current) {
      clearTimeout(domainTransferCompanySearchTimerRef.current);
      domainTransferCompanySearchTimerRef.current = null;
    }
    if (domainTransferCompanySearchResolveRef.current) {
      domainTransferCompanySearchResolveRef.current([]);
      domainTransferCompanySearchResolveRef.current = null;
    }
    domainTransferCompanyPageRef.current = 1;
    domainTransferCompanySearchRef.current = "";
    domainTransferCompanyHasMoreRef.current = true;
    domainTransferCompanyLoadMoreQueuedRef.current = false;
    setPagedDomainTransferCompanyOptions([]);
  }, [setPagedDomainTransferCompanyOptions]);

  const mapDomainTransferCompanyOptions = useCallback(
    (companies: any[]): Option[] => {
      const currentCompanyId = company?.id
        ? String(company.id).toLowerCase()
        : "";

      return companies
        .filter((c: any) => {
          const companyId = c?.id ?? c?.Id;
          return (
            companyId &&
            String(companyId).toLowerCase() !== currentCompanyId
          );
        })
        .map((c: any) => ({
          value: c,
          label:
            c.legalName || c.LegalName || c.name || c.companyName || "",
        }))
        .filter((option: Option) => option.label);
    },
    [company?.id]
  );

  const fetchDomainTransferCompanyPage = useCallback(
    async (
      inputValue: string,
      pageNumber: number
    ): Promise<{ options: Option[]; count: number }> => {
      const response = await rpost(`${DEV_URL}/api/company/getall`, {
        PageNumber: pageNumber,
        PageSize: DOMAIN_TRANSFER_COMPANY_PAGE_SIZE,
        SearchTerm: inputValue || "",
        CompanyID: null,
        ClientID: null,
        LicenseID: null,
        orderBy: null,
        StateCode: null,
        Commodity: null,
        LicenseType: null,
        ascendingOrder: true,
        LicenseStatus: null,
      });

      const companies =
        response?.data?.company ??
        response?.data?.Company ??
        response?.data?.data?.company ??
        response?.data?.data?.Company ??
        response?.data?.data ??
        [];

      if (!Array.isArray(companies)) {
        return { options: [], count: 0 };
      }

      return {
        options: mapDomainTransferCompanyOptions(companies),
        count: companies.length,
      };
    },
    [mapDomainTransferCompanyOptions]
  );

  const loadInitialDomainTransferCompanies = useCallback(async () => {
    if (domainTransferCompanyLoadingRef.current) {
      return;
    }

    setDomainTransferCompanyLoadingState(true);

    try {
      const { options, count } = await fetchDomainTransferCompanyPage("", 1);
      domainTransferCompanyPageRef.current = 1;
      domainTransferCompanySearchRef.current = "";
      domainTransferCompanyHasMoreRef.current =
        count >= DOMAIN_TRANSFER_COMPANY_PAGE_SIZE;
      setPagedDomainTransferCompanyOptions(options);
    } catch (error) {
      console.error("Error loading transfer companies:", error);
      setPagedDomainTransferCompanyOptions([]);
    } finally {
      setDomainTransferCompanyLoadingState(false);
    }
  }, [
    fetchDomainTransferCompanyPage,
    setDomainTransferCompanyLoadingState,
    setPagedDomainTransferCompanyOptions,
  ]);

  const loadDomainTransferCompanyOptions = useCallback(
    (inputValue: string): Promise<Option[]> => {
      if (domainTransferCompanySearchTimerRef.current) {
        clearTimeout(domainTransferCompanySearchTimerRef.current);
      }
      if (domainTransferCompanySearchResolveRef.current) {
        domainTransferCompanySearchResolveRef.current([]);
        domainTransferCompanySearchResolveRef.current = null;
      }

      return new Promise((resolve) => {
        domainTransferCompanySearchResolveRef.current = resolve;
        domainTransferCompanySearchTimerRef.current = setTimeout(async () => {
          const searchTerm = inputValue || "";
          domainTransferCompanySearchTimerRef.current = null;
          domainTransferCompanySearchResolveRef.current = null;
          setDomainTransferCompanyLoadingState(true);

          try {
            const { options, count } = await fetchDomainTransferCompanyPage(
              searchTerm,
              1
            );
            domainTransferCompanyPageRef.current = 1;
            domainTransferCompanySearchRef.current = searchTerm;
            domainTransferCompanyHasMoreRef.current =
              count >= DOMAIN_TRANSFER_COMPANY_PAGE_SIZE;
            setPagedDomainTransferCompanyOptions(options);
            resolve(options);
          } catch (error) {
            console.error("Error loading transfer companies:", error);
            setPagedDomainTransferCompanyOptions([]);
            resolve([]);
          } finally {
            setDomainTransferCompanyLoadingState(false);
          }
        }, 400);
      });
    },
    [
      fetchDomainTransferCompanyPage,
      setDomainTransferCompanyLoadingState,
      setPagedDomainTransferCompanyOptions,
    ]
  );

  const handleDomainTransferCompanyScrollToBottom = useCallback(async () => {
    if (!domainTransferCompanyHasMoreRef.current) {
      return;
    }

    if (domainTransferCompanyLoadingRef.current) {
      domainTransferCompanyLoadMoreQueuedRef.current = true;
      return;
    }

    domainTransferCompanyLoadMoreQueuedRef.current = false;
    setDomainTransferCompanyLoadingState(true);

    try {
      const nextPage = domainTransferCompanyPageRef.current + 1;
      const { options, count } = await fetchDomainTransferCompanyPage(
        domainTransferCompanySearchRef.current,
        nextPage
      );
      domainTransferCompanyPageRef.current = nextPage;
      domainTransferCompanyHasMoreRef.current =
        count >= DOMAIN_TRANSFER_COMPANY_PAGE_SIZE;

      const existingCompanyIds = new Set(
        domainTransferCompanyOptionsRef.current.map((option: Option) =>
          String(option?.value?.id ?? option?.value?.Id ?? "")
        )
      );
      const nextOptions = [
        ...domainTransferCompanyOptionsRef.current,
        ...options.filter(
          (option: Option) =>
            !existingCompanyIds.has(
              String(option?.value?.id ?? option?.value?.Id ?? "")
            )
        ),
      ];

      setPagedDomainTransferCompanyOptions(nextOptions);
    } catch (error) {
      console.error("Error loading more transfer companies:", error);
    } finally {
      setDomainTransferCompanyLoadingState(false);
    }
  }, [
    fetchDomainTransferCompanyPage,
    setDomainTransferCompanyLoadingState,
    setPagedDomainTransferCompanyOptions,
  ]);

  useEffect(() => {
    if (
      domainTransferCompanyLoading ||
      !domainTransferCompanyLoadMoreQueuedRef.current ||
      !domainConfirmModal.show ||
      domainConfirmModal.action !== "remove-mapping"
    ) {
      return;
    }

    domainTransferCompanyLoadMoreQueuedRef.current = false;
    handleDomainTransferCompanyScrollToBottom();
  }, [
    domainConfirmModal.action,
    domainConfirmModal.show,
    domainTransferCompanyLoading,
    handleDomainTransferCompanyScrollToBottom,
  ]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      if (domainTransferCompanySearchTimerRef.current) {
        clearTimeout(domainTransferCompanySearchTimerRef.current);
        domainTransferCompanySearchTimerRef.current = null;
      }
      if (domainTransferCompanySearchResolveRef.current) {
        domainTransferCompanySearchResolveRef.current([]);
        domainTransferCompanySearchResolveRef.current = null;
      }
    };
  }, []);

  // Fetch hierarchy when modal opens
  const fetchHierarchy = useCallback(async () => {
    if (!company?.id) return;

    try {
      setLoadingHierarchy(true);
      const response = await getCompanyHierarchy(company.id);

      if (
        response?.data?.data &&
        Array.isArray(response.data.data) &&
        response.data.data.length > 0
      ) {
        const tree = buildHierarchyTree(response.data.data, company.id);
        setHierarchyTree(tree);
      } else {
        setHierarchyTree(null);
      }
    } catch (error) {
      console.error("Error fetching hierarchy:", error);
      setHierarchyTree(null);
    } finally {
      setLoadingHierarchy(false);
    }
  }, [company?.id]);

  // Fetch hierarchy logs when modal opens
  const fetchHierarchyLogs = useCallback(async () => {
    if (!company?.id) return;

    try {
      setLoadingHierarchyLogs(true);
      const response = await getCompanyHierarchyLogs(company.id);

      if (response?.data?.data) {
        // Transform API response to match UI structure
        // Handle both camelCase (from API) and PascalCase property names
        const transformedLogs = response.data.data
          .map((log: any) => ({
            id: log.id || log.Id,
            action: log.action || log.Action || "",
            initiatedFrom: log.initiatedFrom || log.InitiatedFrom || "",
            actionCompanyName:
              log.actionCompanyName || log.ActionCompanyName || "",
            actionCompanyId: log.actionCompanyId || log.ActionCompanyId || "",
            actionCompanyDomain:
              log.actionCompanyDomain || log.ActionCompanyDomain || "",
            newParentName: log.newParentName || log.NewParentName || "",
            newParentId: log.newParentId || log.NewParentId || "",
            newParentDomain: log.newParentDomain || log.NewParentDomain || "",
            oldParentName: log.oldParentName || log.OldParentName || "",
            oldParentId: log.oldParentId || log.OldParentId || "",
            oldParentDomain: log.oldParentDomain || log.OldParentDomain || "",
            changedBy: log.changedBy || log.ChangedBy || "",
            changedByEmail: log.changedByEmail || log.ChangedByEmail || "",
            changedDate: log.changedDate || log.ChangedDate,
            fromText: log.fromText || log.FromText || "",
            toText: log.toText || log.ToText || "",
            domain: log.domain || log.Domain || "",
            licenseId: log.licenseId || log.LicenseId || "",
            licenseNumber: log.licenseNumber || log.LicenseNumber || "",
            licenseType: log.licenseType || log.LicenseType || "",
            stateCode: log.stateCode || log.StateCode || "",
            commodity: log.commodity || log.Commodity || "",
          }))
          .filter((log: any) => log.action); // Filter out logs without action
        setHierarchyLogs(transformedLogs);
      }
    } catch (error) {
      console.error("Error fetching hierarchy logs:", error);
      setHierarchyLogs([]);
    } finally {
      setLoadingHierarchyLogs(false);
    }
  }, [company?.id]);

  // Fetch logs when modal opens
  useEffect(() => {
    if (showHierarchyLogsModal && company?.id) {
      fetchHierarchyLogs();
    }
  }, [showHierarchyLogsModal, company?.id, fetchHierarchyLogs]);

  // Fetch hierarchy when modal opens
  useEffect(() => {
    if (showHierarchyModal && company?.id) {
      fetchHierarchy();
    }
  }, [showHierarchyModal, company?.id, fetchHierarchy]);

  const fetchDomains = useCallback(async () => {
    if (!company?.id) {
      setPrimaryDomain(null);
      setDomains([]);
      setShowAllDomains(false);
      setDomainIdMap({});
      setDomainBrandLogoMap({});
      return;
    }

    try {
      const response = await rget(
        `${DEV_URL}/api/company/GetCompanyDomains/${company.id}`
      );
      const apiDomains = Array.isArray(response?.data?.data)
        ? response.data.data
        : [];

      if (apiDomains.length === 0) {
        setPrimaryDomain(null);
        setDomains([]);
        setShowAllDomains(false);
        setDomainIdMap({});
        setDomainBrandLogoMap({});
        return;
      }

      const mappedDomains = apiDomains
        .map((item: any) => ({
          id: String(item?.id ?? item?.Id ?? ""),
          domain: normalizeDomain(item?.domain ?? item?.Domain),
          brandLogo: item?.brandLogo || item?.BrandLogo || "",
          isPrimary:
            item?.isPrimary === 1 ||
            item?.isPrimary === true ||
            item?.IsPrimary === true,
        }))
        .filter((item: any) => item.domain);

      const primary =
        mappedDomains.find((item: any) => item.isPrimary)?.domain ?? null;
      const secondaryDomains = mappedDomains
        .filter((item: any) => item.domain !== primary)
        .map((item: any) => item.domain)
        .filter(
          (value: string, index: number, self: string[]) =>
            self.indexOf(value) === index
        )
        .sort();

      const nextDomainIdMap: Record<string, string> = {};
      const nextDomainBrandLogoMap: Record<string, string> = {};
      mappedDomains.forEach((item: any) => {
        if (item.id) {
          nextDomainIdMap[item.domain] = item.id;
        }
        if (item.brandLogo) {
          nextDomainBrandLogoMap[item.domain] = item.brandLogo;
        }
      });

      setDomainIdMap(nextDomainIdMap);
      setDomainBrandLogoMap(nextDomainBrandLogoMap);
      setPrimaryDomain(primary);
      setDomains(secondaryDomains);
      setShowAllDomains(false);
    } catch (error) {
      console.error("Error fetching company domains:", error);
      setPrimaryDomain(null);
      setDomains([]);
      setShowAllDomains(false);
      setDomainIdMap({});
      setDomainBrandLogoMap({});
    }
  }, [company?.id]);

  // Fetch domains from API
  useEffect(() => {
    fetchDomains();
  }, [fetchDomains, domainRefreshKey]);

  if (!company) {
    return renderHeaderSkeleton();
  }

  const logo_error = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = e.target as HTMLImageElement;
    target.style.display = "none";
    if (target.nextElementSibling) {
      (target.nextElementSibling as HTMLElement).style.display = "block";
    }
  };

  const handleCopyEcid = async () => {
    const ecid = company?.enerexCompanyId?.trim();

    if (!ecid) {
      return;
    }

    try {
      await navigator.clipboard.writeText(ecid);
      snackbar("ECID copied", "success");
    } catch (error) {
      snackbar("Unable to copy ECID", "error");
    }
  };

  const openParentModal = (mode: "add" | "update") => {
    setParentModalMode(mode);
    setParentError("");
    if (
      mode === "update" &&
      company.parentCompanyId &&
      company.parentCompanyName
    ) {
      setSelectedParent({
        value: {
          id: company.parentCompanyId,
          legalName: company.parentCompanyName,
          emailDomain: company.parentCompanyDomain,
        },
        label: company.parentCompanyName,
      });
    } else {
      setSelectedParent(null);
    }
    setShowParentModal(true);
  };

  const closeParentModal = () => {
    // Clear any pending debounce timer when closing modal
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setShowParentModal(false);
  };

  const handleParentChange = (option: Option | null) => {
    setSelectedParent(option);
    setParentError("");
  };

  const validateParentSelection = (): boolean => {
    if (!selectedParent || !selectedParent.value || !selectedParent.value.id) {
      setParentError("Please select a parent company.");
      return false;
    }

    // Validate: Cannot select self as parent
    if (selectedParent.value.id === company.id) {
      setParentError("A company cannot be its own parent.");
      return false;
    }

    // Validate: Cannot select blank/empty company
    if (
      !selectedParent.value.legalName ||
      selectedParent.value.legalName.trim() === ""
    ) {
      setParentError("Please select a valid parent company.");
      return false;
    }

    return true;
  };

  const handleSaveParent = async () => {
    if (!validateParentSelection()) {
      return;
    }

    try {
      setParentError("");
      const response = await updateCompanyHierarchy([
        {
          CompanyId: company.id,
          NewParentId: selectedParent.value.id,
          ActionType:
            parentModalMode === "add" ? "added_parent" : "updated_parent",
        },
      ]);

      if (response?.data?.success) {
        snackbar(
          parentModalMode === "add"
            ? "Parent company added successfully"
            : "Parent company updated successfully",
          "success"
        );
        // Close the modal first
        setShowParentModal(false);
        // Refresh company data to get updated parent info
        if (onCompanyUpdate) {
          await onCompanyUpdate();
        } else {
          window.location.reload();
        }
      } else {
        const errorMessage =
          response?.data?.message || "Failed to update parent company";
        setParentError(errorMessage);
        snackbar(errorMessage, "error");
      }
    } catch (error) {
      console.error("Error updating parent:", error);
      const errorMessage = "An error occurred while updating parent company";
      setParentError(errorMessage);
      snackbar(errorMessage, "error");
    }
  };

  const handleRemoveParent = async () => {
    if (!company.parentCompanyId) {
      return;
    }

    try {
      setParentError("");
      const response = await updateCompanyHierarchy([
        {
          CompanyId: company.id,
          NewParentId: null,
          ActionType: "removed_parent", // Explicitly indicate this is removing a parent (from child's perspective)
        },
      ]);

      if (response?.data?.success) {
        snackbar("Parent company removed successfully", "success");
        // Close the modal first
        setShowParentModal(false);
        // Refresh company data
        if (onCompanyUpdate) {
          await onCompanyUpdate();
        } else {
          window.location.reload();
        }
      } else {
        const errorMessage =
          response?.data?.message || "Failed to remove parent company";
        setParentError(errorMessage);
        snackbar(errorMessage, "error");
      }
    } catch (error) {
      console.error("Error removing parent:", error);
      const errorMessage = "An error occurred while removing parent company";
      setParentError(errorMessage);
      snackbar(errorMessage, "error");
    }
  };

  const showParentDetails = Boolean(
    company.parentCompanyId && company.parentCompanyName
  );

  const hubspotId = company?.hubspotId?.trim();
  const hubspotUrl =
    HUBSPOT_BASE_URL && hubspotId ? `${HUBSPOT_BASE_URL}${hubspotId}` : "";
  const eiaIdValue = company?.eiaID || "";
  const eiaIds = [
    ...new Set(
      `${eiaIdValue}`
        .split(",")
        .map((eiaId: string) => eiaId.trim())
        .filter(Boolean)
    ),
  ];
  const getHasDomainApplicationMapping = async (
    domainId: string
  ): Promise<boolean | null> => {
    const response = await rpost(
      `${DEV_URL}/api/company/AssetApplicationMapping`,
      {
        AssetId: domainId,
        AssetType: "Domain",
      }
    );

    if (
      !response ||
      response.status < 200 ||
      response.status >= 300 ||
      response?.data?.success === false ||
      response?.data?.Success === false
    ) {
      snackbar(
        response?.data?.message || "Unable to verify domain asset mapping",
        "error"
      );
      return null;
    }

    const resultData =
      response?.data?.data ?? response?.data?.Data ?? response?.data;
    const mappingResult = Array.isArray(resultData)
      ? resultData[0]
      : resultData;
    const hasApplicationMapping =
      mappingResult?.hasApplicationMapping ??
      mappingResult?.HasApplicationMapping ??
      false;

    return (
      hasApplicationMapping === true ||
      hasApplicationMapping === 1 ||
      String(hasApplicationMapping).toLowerCase() === "true"
    );
  };

  const renderCompanyDomainMessage = (
    responseData: any,
    fallbackMessage: string
  ) => {
    const message =
      responseData?.message || responseData?.Message || fallbackMessage;
    const responseCompanyId =
      responseData?.companyId || responseData?.CompanyId || "";

    if (!responseCompanyId) {
      return message;
    }

    const messageCompanyMatch = /^(.*?\bfor)(?:\s+this)?\s+(.+)$/i.exec(
      message
    );
    const responseCompanyName =
      responseData?.companyName ||
      responseData?.CompanyName ||
      "";
    const messageCompanyName =
      messageCompanyMatch?.[2]?.trim() ||
      responseCompanyName ||
      "";
    const companyLink = (
      <a
        href={`/companies/${responseCompanyId}`}
        target="_blank"
        rel="noreferrer"
        style={{ color: "inherit", textDecoration: "underline" }}
      >
        {messageCompanyName || "company"}
      </a>
    );

    if (messageCompanyMatch && messageCompanyName) {
      return (
        <span>
          {messageCompanyMatch[1]} {companyLink}
        </span>
      );
    }

    if (!messageCompanyName) {
      return (
        <span>
          {message} {companyLink}
        </span>
      );
    }

    const messageIndex = message
      .toLowerCase()
      .lastIndexOf(messageCompanyName.toLowerCase());

    if (messageIndex === -1) {
      return (
        <span>
          {message} for {companyLink}
        </span>
      );
    }

    return (
      <span>
        {message.slice(0, messageIndex)}
        {companyLink}
        {message.slice(messageIndex + messageCompanyName.length)}
      </span>
    );
  };

  const normalizeAssociatedCompanies = (responseData: any) => {
    const rawCompanies =
      responseData?.associatedCompanies ||
      responseData?.AssociatedCompanies ||
      responseData?.data?.associatedCompanies ||
      responseData?.data?.AssociatedCompanies ||
      [];

    const companies = Array.isArray(rawCompanies)
      ? rawCompanies
      : rawCompanies
      ? [rawCompanies]
      : [];

    return companies
      .map((item: any) => {
        const domainId = item?.id || item?.Id || item?.domainId || item?.DomainId;
        const companyId = item?.companyId || item?.CompanyId;

        return {
          id: String(domainId || companyId || ""),
          domainId: String(domainId || ""),
          companyId: String(companyId || ""),
          companyName:
            item?.companyName ||
            item?.CompanyName ||
            item?.legalName ||
            item?.LegalName ||
            "Company",
          companyStatus: item?.companyStatus || item?.CompanyStatus || "",
          domain: item?.domain || item?.Domain || "",
        };
      })
      .filter((item: DomainAssociation) => item.domainId && item.companyId);
  };

  const normalizeDomainDependencies = (items: any[], type: "Contact" | "Brand") =>
    (Array.isArray(items) ? items : [])
      .map((item: any) => ({
        id: String(item?.id || item?.Id || ""),
        type,
        name: item?.name || item?.Name || (type === "Contact" ? "Contact" : "Brand"),
        detail: item?.detail || item?.Detail || "",
        companyId: item?.companyId || item?.CompanyId || company?.id || "",
        companyName: item?.companyName || item?.CompanyName || company?.legalName || "",
      }))
      .filter((item: DomainDependency) => item.id);

  const getCompanyDetailsUrl = (companyId?: string | null) =>
    companyId ? `/companies/${companyId}` : "/companies";

  const closeDuplicateDomainModal = () => {
    setDuplicateDomainModal({ show: false, domain: "", companies: [] });
    setSelectedDuplicateDomainIds({});
  };

  const closeDomainDependencyModal = () => {
    setDomainDependencyModal({ show: false, domain: "", contacts: [], brands: [] });
  };

  const renderActionButtons = (
    containerClassName = "d-flex align-items-center gap-1"
  ) => (
    <Div className={containerClassName}>
      {isSystemAdmin && (
        <Button
          variant="primary"
          size="sm"
          style={{
            padding: "2px 6px",
            fontSize: "10px",
            lineHeight: "1.2",
            display: "inline-flex",
            alignItems: "center",
            gap: "3px",
          }}
          onClick={() => openParentModal(showParentDetails ? "update" : "add")}
        >
          {showParentDetails ? (
            <>
              <EditIcon style={{ fontSize: "9px" }} />
              <small>Update parent</small>
            </>
          ) : (
            <>
              <PlusIcon style={{ fontSize: "9px" }} />
              <small>Add parent</small>
            </>
          )}
        </Button>
      )}
      {isSystemAdmin && (
        <>
          <Button
            variant="outline-primary"
            size="sm"
            style={{
              padding: "2px 6px",
              fontSize: "10px",
              lineHeight: "1.2",
              display: "inline-flex",
              alignItems: "center",
              gap: "3px",
            }}
            className="ms-1"
            onClick={() => setShowHierarchyModal(true)}
          >
            <TeamHierarchyIcon style={{ fontSize: "11px" }} />
            <small>View hierarchy</small>
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            style={{
              padding: "2px 6px",
              fontSize: "10px",
              lineHeight: "1.2",
              display: "inline-flex",
              alignItems: "center",
              gap: "3px",
            }}
            className="ms-1"
            onClick={() => setShowHierarchyLogsModal(true)}
          >
            <i className="mdi mdi-history" style={{ fontSize: "11px" }}></i>
            <small>Change logs</small>
          </Button>

          {hubspotUrl && (
            <Button
              variant="outline-primary"
              size="sm"
              style={{
                padding: "2px 6px",
                fontSize: "10px",
                lineHeight: "1.2",
                display: "inline-flex",
                alignItems: "center",
                gap: "3px",
              }}
              className="ms-1"
              onClick={() => {

                const a = document.createElement("a");
                a.href = hubspotUrl;
                a.target = "_blank";
                a.rel = "noopener noreferrer";
                a.click();
              }}
            >
              <i className="mdi mdi-hubspot" style={{ color: "#ff7a59" }}></i>
              <small>HubSpot</small>
            </Button>
          )}
          {company?.enerexCompanyId && (
            <button
              type="button"
              aria-label="Copy ECID"
              title="Copy ECID"
              onClick={handleCopyEcid}
              className="ms-1 text-muted"
              style={{
                alignSelf: "center",
                background: "transparent",
                border: "1px solid #ced4da",
                borderRadius: "4px",
                display: "inline-flex",
                alignItems: "center",
                gap: "3px",
                padding: "1px 5px",
                fontSize: "10px",
                lineHeight: "1.2",
                whiteSpace: "nowrap",
                cursor: "pointer",
              }}
            >
              <i className="uil uil-copy" aria-hidden="true"></i>
              <small>{company.enerexCompanyId}</small>
            </button>
          )}
        </>
      )}
    </Div>
  );

  const renderParentLogo = () => {
    if (!company.parentCompanyDomain) {
      return <CustomerIcon fontSize={25} className="me-1" />;
    }

    return (
      <span className="me-1 d-inline-flex align-items-center">
        <img
          className="copnay-icons"
          style={{ height: 25, width: 25, objectFit: "contain" }}
          onError={logo_error}
          src={buildCompanyLogoUrl(company.parentCompanyDomain)}
          alt={`${company.parentCompanyName} logo`}
        />
        <CustomerIcon fontSize={25} style={{ display: "none" }} />
      </span>
    );
  };

  const handleSetPrimaryDomain = async (domain: string) => {
    const normalizedDomain = normalizeDomain(domain);
    const domainId = domainIdMap[normalizedDomain];
    if (!domainId) {
      snackbar("Unable to set primary domain", "error");
      return;
    }

    try {
      setIsDomainActionLoading(true);
      const response = await rput(
        `${DEV_URL}/api/company/${company.id}/Domain/${domainId}/SetPrimary`,
        {
          IsPrimary: true,
        }
      );

      if (response?.status === 200 || response?.data?.success) {
        await fetchDomains();
        snackbar("Primary domain updated", "success");
      } else {
        snackbar(
          response?.data?.message || "Failed to update primary domain",
          "error"
        );
      }
    } catch (error) {
      console.error("Error updating primary domain:", error);
      snackbar("An error occurred while updating primary domain", "error");
    } finally {
      setIsDomainActionLoading(false);
    }
  };

  const handleRemoveDomain = async (
    domain: string,
    skipApplicationMappingCheck = false,
    dependencyPayload?: { ContactIds?: string[]; BrandIds?: string[] }
  ): Promise<boolean> => {
    const normalizedDomain = normalizeDomain(domain);
    const domainId = domainIdMap[normalizedDomain];
    if (!domainId) {
      snackbar("Unable to remove domain", "error");
      return true;
    }

    try {
      setIsDomainActionLoading(true);
      if (!skipApplicationMappingCheck) {
        const hasApplicationMapping = await getHasDomainApplicationMapping(
          domainId
        );
        if (hasApplicationMapping === null) {
          return true;
        }
        if (hasApplicationMapping) {
          setSelectedDomainTransferCompany(null);
          setDomainTransferError("");
          resetDomainTransferCompanyOptions();
          setDomainConfirmModal({
            show: true,
            action: "remove-mapping",
            domain,
          });
          loadInitialDomainTransferCompanies();
          return false;
        }
      }

      const response = await rpost(
        `${DEV_URL}/api/company/${company.id}/Domain/${domainId}`,
        dependencyPayload || {},
        {
          headers: { "Content-Type": "application/json" },
        }
          
      );

      if (response?.status === 200 || response?.data?.success) {
        await fetchDomains();
        snackbar("Domain removed", "success");
      } else {
        snackbar(response?.data?.message || "Failed to remove domain", "error");
      }
    } catch (error) {
      console.error("Error removing domain:", error);
      snackbar("An error occurred while removing domain", "error");
    } finally {
      setIsDomainActionLoading(false);
    }
    return true;
  };

  const getDomainDependencies = async (
    domainId: string
  ): Promise<{ contacts: DomainDependency[]; brands: DomainDependency[] } | null> => {
    const response = await rget(
      `${DEV_URL}/api/company/${company.id}/Domain/${domainId}/Dependencies`
    );

    if (
      !response ||
      response.status < 200 ||
      response.status >= 300 ||
      response?.data?.success === false ||
      response?.data?.Success === false
    ) {
      snackbar(
        response?.data?.message || "Unable to verify domain dependencies",
        "error"
      );
      return null;
    }

    const data = response?.data?.data || response?.data?.Data || {};
    return {
      contacts: normalizeDomainDependencies(
        data?.contacts || data?.Contacts || [],
        "Contact"
      ),
      brands: normalizeDomainDependencies(data?.brands || data?.Brands || [], "Brand"),
    };
  };

  const handleRemoveDomainRequest = async (domain: string) => {
    const normalizedDomain = normalizeDomain(domain);
    const domainId = domainIdMap[normalizedDomain];
    if (!domainId) {
      snackbar("Unable to remove domain", "error");
      return;
    }

    try {
      setIsDomainActionLoading(true);
      const hasApplicationMapping = await getHasDomainApplicationMapping(
        domainId
      );

      if (hasApplicationMapping === null) {
        return;
      }

      if (hasApplicationMapping) {
        setSelectedDomainTransferCompany(null);
        setDomainTransferError("");
        resetDomainTransferCompanyOptions();
        setDomainConfirmModal({
          show: true,
          action: "remove-mapping",
          domain,
        });
        loadInitialDomainTransferCompanies();
        return;
      }

      const dependencies = await getDomainDependencies(domainId);
      if (!dependencies) {
        return;
      }

      if (dependencies.brands.length > 0) {
        setDomainDependencyModal({
          show: true,
          domain,
          contacts: [],
          brands: dependencies.brands,
        });
        return;
      }

      setDomainConfirmModal({
        show: true,
        action: "remove",
        domain,
      });
    } catch (error) {
      console.error("Error checking domain asset mapping:", error);
      snackbar("An error occurred while checking domain asset mapping", "error");
    } finally {
      setIsDomainActionLoading(false);
    }
  };

  const addCompanyDomain = async (normalizedDomain: string) => {
    try {
      setIsDomainActionLoading(true);
      const response = await rpost(
        `${DEV_URL}/api/company/${company.id}/Domain`,
        {
          Domain: normalizedDomain,
        }
      );

      if (response?.status === 200 || response?.data?.success) {
        setNewDomainValue("");
        setIsAddingDomain(false);
        await fetchDomains();
        snackbar(
          response?.data?.message ||
            response?.data?.Message ||
            "Domain added successfully",
          "success",
          undefined,
          10000
        );
      } else {
        const associatedCompanies = normalizeAssociatedCompanies(response?.data);
        if (associatedCompanies.length > 0) {
          setDuplicateDomainModal({
            show: true,
            domain: normalizedDomain,
            companies: associatedCompanies,
          });
          setSelectedDuplicateDomainIds({});
          return false;
        }

        snackbar(
          renderCompanyDomainMessage(response?.data, "Failed to add domain"),
          "error",
          undefined,
          10000
        );
      }
    } catch (error) {
      console.error("Error adding domain:", error);
      snackbar("An error occurred while adding domain", "error");
    } finally {
      setIsDomainActionLoading(false);
    }
    return true;
  };

  const handleAddDomain = async () => {

    const normalizedDomain = normalizeDomain(newDomainValue);

    if (hasDomainSpecialCharacters(normalizedDomain)) {
      snackbar("Enter a domain only (e.g., google.com), without https://, paths, or email addresses", "error");
      return;
    }

    if (!normalizedDomain || !normalizedDomain.includes(".")) {
      return;
    }

    await addCompanyDomain(normalizedDomain);
  };

  const handleTransferDomain = async () => {
    const normalizedDomain = normalizeDomain(domainConfirmModal.domain);
    const domainId = domainIdMap[normalizedDomain];
    const targetCompanyId =
      selectedDomainTransferCompany?.value?.id ??
      selectedDomainTransferCompany?.value?.Id;

    if (!domainId) {
      setDomainTransferError("Unable to transfer domain");
      return;
    }

    if (!targetCompanyId) {
      setDomainTransferError("Please select a company");
      return;
    }

    try {
      setIsDomainTransferLoading(true);
      setDomainTransferError("");

      const response = await rpost(
        `${DEV_URL}/api/company/TransferDomain`,
        {
          DomainId: domainId,
          AssetId: domainId,
          AssetType: "Domain",
          CompanyId: company.id,
          SourceCompanyId: company.id,
          TargetCompanyId: targetCompanyId,
        }
      );

      if (
        response?.status === 200 &&
        response?.data?.success !== false &&
        response?.data?.Success !== false
      ) {
        snackbar("Domain transferred successfully", "success");
        closeDomainConfirmModal();
        await fetchDomains();
      } else {
        setDomainTransferError(
          response?.data?.message ||
            response?.data?.Message ||
            "Failed to transfer domain"
        );
      }
    } catch (error) {
      console.error("Error transferring domain:", error);
      setDomainTransferError("An error occurred while transferring domain");
    } finally {
      setIsDomainTransferLoading(false);
    }
  };

  const closeDomainConfirmModal = () => {
    setDomainConfirmModal({ show: false, action: null, domain: "" });
    setSelectedDomainTransferCompany(null);
    setDomainTransferError("");
    resetDomainTransferCompanyOptions();
  };

  const handleDuplicateDomainConfirm = async () => {
    const selectedCompanies = duplicateDomainModal.companies.filter(
      (item) => selectedDuplicateDomainIds[item.domainId]
    );

    if (selectedCompanies.length === 0) {
      snackbar("Please select at least one company", "error");
      return;
    }

    try {
      setIsDomainActionLoading(true);

      for (const item of selectedCompanies) {
        const response = await rpost(
          `${DEV_URL}/api/company/${item.companyId}/Domain/${item.domainId}`,
          {},
          { headers: { "Content-Type": "application/json" } }
        );

        if (
          !response ||
          response.status < 200 ||
          response.status >= 300 ||
          response?.data?.success === false ||
          response?.data?.Success === false
        ) {
          snackbar(
            response?.data?.message ||
              `Failed to remove domain from ${item.companyName}`,
            "error"
          );
          return;
        }
      }

      const domain = duplicateDomainModal.domain;
      closeDuplicateDomainModal();
      await addCompanyDomain(domain);
    } catch (error) {
      console.error("Error resolving duplicate domain:", error);
      snackbar("An error occurred while resolving duplicate domain", "error");
    } finally {
      setIsDomainActionLoading(false);
    }
  };

  const handleDomainConfirmProceed = async () => {
    const { action, domain } = domainConfirmModal;

    if (action === "remove") {
      const shouldCloseModal = await handleRemoveDomain(domain, true);
      if (shouldCloseModal) {
        closeDomainConfirmModal();
      }
      return;
    }

    if (action === "primary") {
      await handleSetPrimaryDomain(domain);
      closeDomainConfirmModal();
    }
  };

  const displayedSecondaryDomains = showAllDomains
    ? domains
    : domains.slice(0, 2);
  const renderDomainPill = (domain: string, isMain = false) => {
    const normalizedDomain = normalizeDomain(domain);
    const isPrimary = normalizedDomain === primaryDomain;

    return isPremium ? (
      <Badge
        pill
        bg=""
        className={`me-1 ${
          isMain ? "main-domain-badge" : "secondary-domain-badge"
        } domain-badge-pill ${isPrimary ? "domain-badge-primary" : ""}`}
      >
        <span className="domain-badge-content">
          <Text
            variant="link"
            url={`http://${domain}`}
            target="_blank"
            rel="noopener noreferrer"
            className="domain-badge-link d-inline-flex align-items-center"
          >
            <Text variant="h6" className="m-0">
              {domain}
            </Text>
          </Text>
          {isSystemAdmin && (
            <span
              className="domain-pill-actions"
              role="group"
              aria-label={`Actions for ${domain}`}
            >
              <button
                type="button"
                className={`domain-pill-icon-btn ${
                  isPrimary ? "is-primary" : ""
                }`}
                disabled={isDomainActionLoading || isPrimary}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!isPrimary) {
                    setDomainConfirmModal({
                      show: true,
                      action: "primary",
                      domain,
                    });
                  }
                }}
                aria-label={
                  isPrimary
                    ? `Primary domain: ${domain}`
                    : `Make ${domain} primary`
                }
              >
                <FontAwesomeIcon
                  icon={isPrimary ? faStarSolid : faStarRegular}
                />
              </button>
              {
                <button
                  type="button"
                  className="domain-pill-icon-btn"
                  disabled={isDomainActionLoading}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRemoveDomainRequest(domain);
                  }}
                  aria-label={`Remove ${domain}`}
                >
                  <CloseIcon />
                </button>
              }
            </span>
          )}
        </span>
      </Badge>
    ) : (
      <Badge
        pill
        bg=""
        className="me-1 secondary-domain-badge domain-badge-pill"
        style={{
          cursor: "default",
          textDecoration: "none",
          padding: "0.35em 0.7em",
          pointerEvents: "none",
        }}
      >
        <Text variant="h6" className="m-0">
          XXXXXX
        </Text>
      </Badge>
    );
  };

  const renderAddDomainPill = () => {
    if (!isPremium) {
      return null;
    }

    if (isAddingDomain) {
      return (
        <Badge pill bg="" className="me-1 domain-badge-pill domain-add-pill">
          <span className="domain-badge-content">
            <input
              className="domain-add-input"
              value={newDomainValue}
              onChange={(e) => setNewDomainValue(e.target.value)}
              placeholder="add domain"
              onBlur={() => {
                if (!newDomainValue.trim()) {
                  setIsAddingDomain(false);
                  setNewDomainValue("");
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddDomain();
                }
                if (e.key === "Escape") {
                  setIsAddingDomain(false);
                  setNewDomainValue("");
                }
              }}
              autoFocus
            />
            <button
              type="button"
              className="domain-pill-icon-btn"
              disabled={isDomainActionLoading}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleAddDomain();
              }}
              aria-label="Add domain"
            >
              <PlusIcon />
            </button>
          </span>
        </Badge>
      );
    }

    return (
      <Badge pill bg="" className="me-1 domain-badge-pill domain-add-pill">
        <button
          type="button"
          className="domain-add-trigger"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsAddingDomain(true);
          }}
          aria-label="Add domain"
        >
          <Text variant="h6" className="m-0">
            Add Domain
          </Text>
          <PlusIcon />
        </button>
      </Badge>
    );
  };

  const renderedDomains = [
    ...(primaryDomain ? [primaryDomain] : []),
    ...displayedSecondaryDomains,
  ];
  const primaryDomainLogo =
    primaryDomain && domainBrandLogoMap[primaryDomain]
      ? domainBrandLogoMap[primaryDomain]
      : primaryDomain
      ? buildCompanyLogoUrl(primaryDomain)
      : "";
  const renderEiaIds = () => {
    if (eiaIds.length === 0) {
      return null;
    }

    return (
      <Div
        className="d-flex align-items-center justify-content-end flex-wrap gap-1"
        style={{
          width: "fit-content",
          maxWidth: "100%",
          minHeight: 44,
          marginTop: 16,
          marginRight: 0,
          marginLeft: "auto",
          padding: "10px 16px",
          border: "1px solid #dde2f2",
          borderRadius: 6,
          backgroundColor: "#fff",
        }}
      >
        <strong
          className="me-1"
          style={{
            color: "#1f2d3d",
            fontSize: 12,
            lineHeight: "22px",
            whiteSpace: "nowrap",
          }}
        >
          EIA IDs :
        </strong>
        {eiaIds.map((eiaId) => (
          <span
            key={eiaId}
            style={{
              display: "inline-flex",
              alignItems: "center",
              minHeight: 22,
              padding: "2px 10px",
              border: "1px solid #bda7ff",
              borderRadius: 4,
              backgroundColor: "#f3efff",
              color: "#3b188f",
              fontSize: 12,
              fontWeight: 700,
              lineHeight: "16px",
            }}
          >
            {eiaId}
          </span>
        ))}
      </Div>
    );
  };

  return (
    <>
      <DetailsHeader
        /* icon={<CustomerIcon style={{ height: '50px' }} />} */
        goBackOnClick={() => {
          // Navigate to /companies page instead of going back
          navigate("/companies");
        }}
        icon={
          primaryDomain === undefined ||
          primaryDomain === null ||
          primaryDomain === "" ? (
            <CustomerIcon className="me-2" fontSize={64} />
          ) : (
            <span>
              <img
                className="copnay-icons me-2"
                onError={logo_error}
                src={primaryDomainLogo}
                alt={`${company.legalName} logo`}
              />
              <CustomerIcon fontSize={64} style={{ display: "none" }} />
            </span>
          )
        }
        pageTitle={company.legalName}
        title={
          <Div className="d-flex flex-column">
            <DetailsHeader.Title title={company.legalName} />
            {(primaryDomain || domains.length > 0 || isPremium) && (
              <Div className="d-flex align-items-center flex-wrap gap-1">
                {renderedDomains.map((domain) =>
                  renderDomainPill(domain, domain === primaryDomain)
                )}
                {isSystemAdmin && renderAddDomainPill()}
                {domains.length > 2 && !showAllDomains && (
                  <>
                    <Button
                      variant="link"
                      className="p-0"
                      style={{
                        color: "#7083d7",
                        textDecoration: "none",
                        padding: "0 !important",
                        margin: "0 !important",
                        fontSize: "0.9rem",
                        fontWeight: "normal",
                      }}
                      onClick={() => setShowAllDomains(true)}
                    >
                      show more
                    </Button>
                  </>
                )}
              </Div>
            )}
            <Div className="d-flex align-items-center mt-1">
              {showParentDetails && (
                <>
                  <Text variant="plain" className="mb-0 me-1">
                    Child of
                  </Text>
                  {renderParentLogo()}
                  <Link
                    to={`/companies/${company.parentCompanyId}`}
                    className="text-decoration-none"
                    style={{ color: "#7083d7", fontSize: "14px" }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {company.parentCompanyName}
                  </Link>
                </>
              )}
              {!showParentDetails &&
                renderActionButtons("d-flex align-items-center ms-2 gap-1")}
            </Div>
            {showParentDetails &&
              renderActionButtons("d-flex align-items-center mt-1 gap-1")}
          </Div>
        }
        subtitle={null}
        detailFields={
          company.companyType ? (
            <Text variant="plain">{company.companyType}</Text>
          ) : null
        }
        rightBox={
          <Div>
            {renderEiaIds()}
            <Div className="d-flex justify-content-end">
              {/* <Div className="d-flex flex-column"> */}
              {/* <TextLine
                            primary="Company Since:"
                            secondary={"10/22/2012"}
                        />
                        <Div>
                            <strong>Status: </strong>
                            <StatusBadge.Company status={"Active"} />
                        </Div>
                        <Div>
                            <strong>Last Event: </strong>
                            {"Company (10443720006383440) was added" ? (
                                <PopoverTooltip
                                    text={"Account"}
                                    placement="left"
                                />
                            ) : (
                                '-'
                            )}
                        </Div> */}

              {/*  <TextLine
                            primary="Last Event:"
                            secondary={company.lastEventLog || '-'}
                        /> */}
              {/* </Div> */}
              {/* <TextLine
                        primary={trimInvalidDate(company.createdDate && company.createdDate.split("T")[0]) == '' ? '' : "Last Updated date:"}
                        secondary={trimInvalidDate(company.createdDate && company.createdDate.split("T")[0])}
                        /> */}
            </Div>
          </Div>
        }
      />
      <AddUpdateParentModal
        show={showParentModal}
        mode={parentModalMode}
        company={company}
        selectedParent={selectedParent}
        parentError={parentError}
        onClose={closeParentModal}
        onParentChange={handleParentChange}
        onSave={handleSaveParent}
        onRemove={handleRemoveParent}
        loadParentOptions={loadParentOptions}
      />
      <ViewHierarchyModal
        show={showHierarchyModal}
        loading={loadingHierarchy}
        hierarchyTree={hierarchyTree}
        onClose={() => setShowHierarchyModal(false)}
        logo_error={logo_error}
      />

      <HierarchyLogsModal
        show={showHierarchyLogsModal}
        loading={loadingHierarchyLogs}
        logs={hierarchyLogs}
        company={company}
        onClose={() => setShowHierarchyLogsModal(false)}
        logo_error={logo_error}
      />

      <ConfirmationModal
        dialogClassName="modal-md"
        centered
        show={duplicateDomainModal.show}
        onHide={closeDuplicateDomainModal}
        title="Duplicate Domain"
        confirmButtonLabel="Save"
        confirmButtonVariant="primary"
        isLoading={isDomainActionLoading}
        onConfirm={handleDuplicateDomainConfirm}
      >
        <p className="mb-3">
          <strong>{duplicateDomainModal.domain}</strong> is already associated
          with the following companies. Select the company associations to
          remove before adding it here.
        </p>
        <div className="d-flex flex-column gap-2">
          {duplicateDomainModal.companies.map((item) => (
            <div
              key={item.domainId}
              className="d-flex align-items-start justify-content-between gap-2 border rounded p-2"
            >
              <Form.Check
                id={`duplicate-domain-${item.domainId}`}
                checked={Boolean(selectedDuplicateDomainIds[item.domainId])}
                onChange={(event) =>
                  setSelectedDuplicateDomainIds((current) => ({
                    ...current,
                    [item.domainId]: event.target.checked,
                  }))
                }
                label={
                  <span className="d-flex flex-column">
                    <a
                      href={getCompanyDetailsUrl(item.companyId)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {item.companyName}
                    </a>
                    {item.companyStatus ? (
                      <small className="text-muted">{item.companyStatus}</small>
                    ) : null}
                  </span>
                }
              />
            </div>
          ))}
        </div>
      </ConfirmationModal>

      <ConfirmationModal
        dialogClassName="modal-md"
        centered
        show={domainDependencyModal.show}
        onHide={closeDomainDependencyModal}
        title="Domain Dependencies"
        confirmButtonLabel="Close"
        confirmButtonVariant="secondary"
        isLoading={isDomainActionLoading}
        onConfirm={closeDomainDependencyModal}
      >
        <p className="mb-3">
          Domain <strong>{domainDependencyModal.domain}</strong> is associated
          with the following Brands.
        </p>
        <div className="table-responsive">
          <table className="table table-sm mb-0">
            <thead>
              <tr>
                <th>Brand</th>
                <th>Associated Company</th>
              </tr>
            </thead>
            <tbody>
              {domainDependencyModal.brands.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>
                    <a
                      href={getCompanyDetailsUrl(item.companyId)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {item.companyName || "Company"}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ConfirmationModal>

      <ConfirmationModal
        dialogClassName="modal-md"
        centered
        show={domainConfirmModal.show}
        onHide={closeDomainConfirmModal}
        title={
          domainConfirmModal.action === "primary"
            ? "Set Primary Domain"
            : domainConfirmModal.action === "remove-mapping"
            ? "Transfer Domain"
            : "Remove Domain"
        }
        confirmButtonLabel={
          domainConfirmModal.action === "remove-mapping" ? "Save" : "Proceed"
        }
        confirmButtonVariant="primary"
        isLoading={
          domainConfirmModal.action === "remove-mapping"
            ? isDomainTransferLoading
            : isDomainActionLoading
        }
        onConfirm={
          domainConfirmModal.action === "remove-mapping"
            ? handleTransferDomain
            : handleDomainConfirmProceed
        }
      >
        {domainConfirmModal.action === "remove" ? (
          <p>
            Are you sure you want to remove{" "}
            <strong>{domainConfirmModal.domain}</strong>?
          </p>
        ) : domainConfirmModal.action === "remove-mapping" ? (
          <>
            <p>
              This domain is used in another product for the same company, so
              you cannot delete it. You can either transfer it to another
              company, or first delete/correct the product data, and then delete
              it.
            </p>
            {domainTransferError ? (
              <Alert variant="danger">{domainTransferError}</Alert>
            ) : null}
            <Form.Group className="mb-2">
              <Form.Label>Transfer domain to company</Form.Label>
              <AsyncSelect
                key={domainConfirmModal.domain || "domain-transfer-company"}
                ref={domainTransferSelectRef}
                cacheOptions={false}
                defaultOptions={domainTransferCompanyOptions}
                loadOptions={loadDomainTransferCompanyOptions}
                onMenuOpen={() => {
                  if (domainTransferCompanyOptionsRef.current.length === 0) {
                    loadInitialDomainTransferCompanies();
                  }
                }}
                onMenuScrollToBottom={handleDomainTransferCompanyScrollToBottom}
                value={selectedDomainTransferCompany}
                onChange={(option) => {
                  setSelectedDomainTransferCompany(option);
                  setDomainTransferError("");
                }}
                placeholder="Search company..."
                isClearable
                isSearchable
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                styles={modalSelectStyles}
                isLoading={domainTransferCompanyLoading || isDomainTransferLoading}
                maxMenuHeight={250}
              />
            </Form.Group>
          </>
        ) : (
          <p>
            Are you sure you want to set{" "}
            <strong>{domainConfirmModal.domain}</strong> as the primary domain?
          </p>
        )}
      </ConfirmationModal>
    </>
  );
};

export default CustomerDetailsHeader;
